<template>
  <div class="pdv-checkout-recebimentos-panel" data-testid="pdv-checkout-recebimentos-panel">
    <div class="section-label">{{ t('Recebimentos') }}</div>

    <div v-if="isLoading" class="panel-loading">
      <i class="pi pi-spinner pi-spin"></i>
      <span>{{ t('Carregando...') }}</span>
    </div>

    <div v-else-if="recebimentos.length === 0" class="panel-empty" data-testid="pdv-checkout-recebimentos-empty">
      <i class="pi pi-inbox"></i>
      <span>{{ t('Nenhum recebimento registrado') }}</span>
    </div>

    <div v-else class="recebimentos-list" data-testid="pdv-checkout-recebimentos-list">
      <!-- Uma linha por VENDA: as n parcelas de um cartão parcelado aparecem juntas (chamado 2026-0067). -->
      <div
        v-for="grupo in vendas"
        :key="grupo.chave"
        class="recebimento-row"
        :class="{ 'row-clickable': isEditAllowed }"
        :data-testid="`pdv-checkout-recebimentos-row-${grupo.chave}`"
        @click="isEditAllowed && grupo.chave && handleRowClick(grupo.chave)"
      >
        <div class="recebimento-info">
          <span class="recebimento-date">{{ formatDate(grupo.linhas[0].recebimento_data) }}</span>
          <span class="recebimento-method">
            {{ getFormaPagamentoNome(grupo.linhas[0]) }}
            <span v-if="grupo.numeroParcelas > 1" class="recebimento-parcelas" :data-testid="`pdv-checkout-recebimentos-parcelas-${grupo.chave}`">
              · {{ tp('{n}x', { n: grupo.numeroParcelas }) }}
              <small>{{ tp('{d}/{n} disponíveis', { d: grupo.disponiveis, n: grupo.numeroParcelas }) }}</small>
            </span>
          </span>
        </div>
        <div class="recebimento-actions">
          <span class="recebimento-value">{{ n(grupo.valorTotal / 100, 'currency') }}</span>
          <i
            v-if="isEditAllowed"
            class="pi pi-pencil recebimento-edit-icon"
            data-testid="edit-recebimento-btn"
          />
        </div>
      </div>
    </div>

    <Button
      v-if="!isCancelled && !(isAtendenteOrOperador && valorRestante <= 0)"
      :label="t('Adicionar Recebimento')"
      icon="pi pi-plus"
      size="small"
      severity="success"
      outlined
      :disabled="valorRestante <= 0 && isAtendenteOrOperador"
      :data-testid="`pdv-checkout-recebimentos-add-btn`"
      v-tooltip.top="isAtendenteOrOperador && valorRestante <= 0 ? t('Saldo já quitado') : undefined"
      @click="handleAddRecebimento"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { tComParametros } from '@/utils/tComParametros';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useRecebimento } from '../../composables/useRecebimento';
import { useFormasPagamento } from '../../composables/useFormasPagamento';
import { agrupaParcelas, hojeLocal } from '../../composables/useParcelamento';
import { useUserPermissions } from '../../composables/usePermissions';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface RecebimentoRow {
  recebimento_id?: Id;
  recebimento_data?: string;
  recebimento_valor?: number;
  recebimento_forma_pagamento_id?: number;
  recebimento_forma_pagamento_nome?: string;
  forma_pagamento_nome?: string;
  recebimento_data_disponivel?: string | null;
  recebimento_numero_parcelas?: number;
  recebimento_parcela_numero?: number;
  recebimento_parcela_de?: number | null;
  [key: string]: unknown;
}

interface FormaPagamentoApi {
  forma_pagamento_id: number;
  forma_pagamento_nome: string;
}

const props = defineProps<{
  orderId: Id;
  franchiseId: Id;
  valorRestante: number;
  orderStatus: string;
}>();

const { t, n } = useI18n();

// Chave nova com parâmetros: nunca mostrar "{n}" cru (utils/tComParametros).
const tp = (chave: string, parametros: Record<string, string | number>): string => tComParametros(t, chave, parametros);
const { fetchRecebimentosByOrigem } = useRecebimento();
const { listarFormasPagamento } = useFormasPagamento();

const isLoading = ref(false);
const recebimentos = ref<RecebimentoRow[]>([]);
const formasPagamentoMap = ref<Map<number, string>>(new Map());
const isCancelled = computed(() => props.orderStatus === 'cancelled');
/** As linhas de volta em vendas; clicar abre a parcela 1 (editar/apagar age na venda inteira). */
const vendas = computed(() => agrupaParcelas(recebimentos.value, hojeLocal()));

const getFormaPagamentoNome = (rec: RecebimentoRow): string => {
  if (rec.forma_pagamento_nome) return rec.forma_pagamento_nome;
  if (rec.recebimento_forma_pagamento_nome) return rec.recebimento_forma_pagamento_nome;
  if (rec.recebimento_forma_pagamento_id !== undefined) {
    return formasPagamentoMap.value.get(rec.recebimento_forma_pagamento_id) || '-';
  }
  return '-';
};

const { isManagerOrAbove, isAtendenteOrOperador } = useUserPermissions();
const isEditAllowed = computed(() => isManagerOrAbove.value);

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  const parts = dateStr.split(' ')[0].split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const loadFormasPagamento = async (): Promise<void> => {
  try {
    const formasPagamento = await listarFormasPagamento() as FormaPagamentoApi[];
    const map = new Map<number, string>();
    formasPagamento.forEach((fp: FormaPagamentoApi) => {
      map.set(fp.forma_pagamento_id, fp.forma_pagamento_nome);
    });
    formasPagamentoMap.value = map;
  } catch (err) {
    log('PDVCheckoutRecebimentosPanel', 'loadFormasPagamento', 'Error', err);
  }
};

const loadRecebimentos = async (): Promise<void> => {
  log('PDVCheckoutRecebimentosPanel', 'loadRecebimentos', 'Loading recebimentos for order', props.orderId);
  isLoading.value = true;
  try {
    const data = await fetchRecebimentosByOrigem('so_order', Number(props.orderId));
    recebimentos.value = (data || []) as unknown as RecebimentoRow[];
  } catch (err) {
    log('PDVCheckoutRecebimentosPanel', 'loadRecebimentos', 'Error', err);
    recebimentos.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleRowClick = (recebimentoId: Id): void => {
  eventBus.emit('modal.recebimento.open', { id: recebimentoId });
};

const handleAddRecebimento = (): void => {
  eventBus.emit('modal.recebimento.open', {
    prefilledData: {
      origem: 'so_order',
      origemId: props.orderId,
      franquiaId: props.franchiseId,
      valorRestante: Math.max(0, props.valorRestante) / 100,
    },
  });
};

const handleRefresh = (): void => {
  loadRecebimentos();
};

onMounted(async () => {
  await loadFormasPagamento();
  loadRecebimentos();
  eventBus.on('recebimento.refresh', handleRefresh);
  eventBus.on(`refresh-recebimento-${props.orderId}`, handleRefresh);
  eventBus.on('pdv.saved', handleRefresh);
});

onBeforeUnmount(() => {
  eventBus.off('recebimento.refresh', handleRefresh);
  eventBus.off(`refresh-recebimento-${props.orderId}`, handleRefresh);
  eventBus.off('pdv.saved', handleRefresh);
});
</script>

<style scoped>
.pdv-checkout-recebimentos-panel {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--p-surface-200);
}

.section-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.panel-loading,
.panel-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  color: var(--p-text-muted-color);
  font-size: 0.9rem;
}

.panel-empty i {
  font-size: 1.2rem;
}

.recebimentos-list {
  margin-bottom: 8px;
}

.recebimento-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.recebimento-row:nth-child(odd) {
  background: var(--p-surface-50);
}

.recebimento-row.row-clickable {
  cursor: pointer;
}

.recebimento-row.row-clickable:hover {
  background: var(--p-surface-100);
}

.recebimento-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.recebimento-date {
  font-weight: 600;
  font-size: 0.8rem;
}

.recebimento-method {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.recebimento-parcelas small {
  margin-left: 4px;
  opacity: 0.8;
}

.recebimento-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.recebimento-value {
  font-weight: 700;
  font-size: 0.9rem;
}

.recebimento-edit-icon {
  font-size: 0.85rem;
  color: var(--p-primary-color);
  cursor: pointer;
}
</style>
